import {
  createContext,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useState
} from "react";

import { CaptiveLayout } from "src/layouts/CaptiveLayout/CaptiveLayout";

type CaptiveScreenContent = ReactElement | ReactNode;
type CaptiveScreenCallback = () => CaptiveScreenContent;

interface CaptiveScreenContextSchema {
  openCaptiveScreen: (
    content: CaptiveScreenContent | CaptiveScreenCallback
  ) => void;
  closeCaptiveScreen: () => void;
}

export const CaptiveScreenContext = createContext<CaptiveScreenContextSchema>({
  openCaptiveScreen: () => null,
  closeCaptiveScreen: () => null
});

export const CaptiveScreen = ({ children }: PropsWithChildren) => {
  /**
   * Storing the Component in state is a bit unusual but given
   * that the Component is a wrapper for other component(s) we
   * will be forced to either save the Component or it's content
   * in state.
   */
  const [screen, setScreen] = useState<ReactElement | null>(null);
  /**
   * Since the inner component will need to handle its own
   * data and processing, we will need to provide a way
   * for the inner component to close the screen. this would
   * be accessed from the context from within the inner
   * component.
   */
  const closeCaptiveScreen = useCallback(() => {
    setScreen(null);
  }, []);

  const openCaptiveScreen = useCallback(
    (content: CaptiveScreenContent | CaptiveScreenCallback) => {
      setScreen(
        <CaptiveLayout>
          {content instanceof Function ? content() : content}
        </CaptiveLayout>
      );
    },
    []
  );

  return (
    <CaptiveScreenContext.Provider
      value={{ openCaptiveScreen, closeCaptiveScreen }}
    >
      {children}
      {screen}
    </CaptiveScreenContext.Provider>
  );
};
